// src/pages/Contact.js
import React, { useState, useEffect, useRef } from "react";
import "../styles/Contact.css";
import contactDoodle from "../assets/contact-doodle.png";
import Footer from "../components/Footer";

// Firestore
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

const DEFAULT_EMAIL = "lsulaksha.shetty@gmail.com";

// Fallback inquiry options (no “Other”)
const DEFAULT_INQUIRY_OPTIONS = [
  "Content Strategy",
  "AI Tools Proficiency",
  "Discord Server",
  "UI/UX Design",
  "Presentation Design",
  "Content Writing",
  "Video Editing",
  "Social Media Creatives",
  "Handmade Gifts",
];

// Your provided social links (used as defaults; Firestore can override later)
const DEFAULT_SOCIALS = [
  {
    id: "instagram-1",
    label: "Instagram",
    url: "https://www.instagram.com/sushestudio?igsh=NjF0MmhzZmM5ZTZ0",
    type: "instagram",
  },
  {
    id: "linkedin-1",
    label: "LinkedIn",
    url: "https://www.linkedin.com/in/sulakshashetty/",
    type: "linkedin",
  },
  {
    id: "youtube-1",
    label: "YouTube — Handy Gifts",
    url: "https://www.youtube.com/handygifts",
    type: "youtube",
  },
  {
    id: "youtube-2",
    label: "YouTube — SuShe’s World",
    url: "https://www.youtube.com/sushesworld",
    type: "youtube",
  },
  {
    id: "discord-1",
    label: "Discord",
    url: "https://discord.gg/qmgkeqvcCP",
    type: "discord",
  },
];

const Contact = () => {
  // Visible form fields only (no honeypots here)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiry: "",
    message: "",
  });

  // Honeypot inputs: UNCONTROLLED via refs so console-injected values are detectable
  const websiteRef = useRef(null); // should stay empty
  const tokenRef = useRef(null);   // holds the session token
  const [hpToken] = useState(() => Math.random().toString(36).slice(2));

  // Select options (Firestore-driven + fallback)
  const [inquiryOptions, setInquiryOptions] = useState(DEFAULT_INQUIRY_OPTIONS);

  // Hero + copy from Firestore (pageContent/contact)
  const [pageSettings, setPageSettings] = useState({
    heroImage: "",
    heroTitle: "Keep in touch",
    heroSubtitle:
      "Reach out and let’s explore how I can bring your vision to life.",
    desktopDescription:
      "I would love to hear from you and answer any questions you may have. You can contact me by filling out the form below or sending an email at <strong>lsulaksha.shetty@gmail.com</strong>. You can also follow me on my social media.",
    mobileDescription:
      "I’d love to hear from you! Whether you have questions or just want to connect, feel free to fill out the form below or email me at <strong>lsulaksha.shetty@gmail.com</strong>. You can also follow me on my social media.",
  });

  // Global contact info (siteSettings/contactInfo)
  const [contactInfo, setContactInfo] = useState({
    email: DEFAULT_EMAIL,
    socials: DEFAULT_SOCIALS, // ready for Firestore override
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // 1) Page copy / hero (pageContent/contact)
        const pageRef = doc(db, "pageContent", "contact");
        const pageSnap = await getDoc(pageRef);

        // (Optional) per-page inquiryOptions (not required)
        let perPageOptions = null;

        if (mounted && pageSnap.exists()) {
          const d = pageSnap.data();
          setPageSettings((prev) => ({
            ...prev,
            heroImage: d.heroBackground || d.heroImage || prev.heroImage,
            heroTitle: d.heroTitle || prev.heroTitle,
            heroSubtitle: d.heroSubtitle || prev.heroSubtitle,
            desktopDescription: d.desktopDescription || prev.desktopDescription,
            mobileDescription: d.mobileDescription || prev.mobileDescription,
          }));

          if (Array.isArray(d.inquiryOptions) && d.inquiryOptions.length) {
            perPageOptions = d.inquiryOptions;
          }
        }

        // 2) Global contact info (siteSettings/contactInfo)
        const infoRef = doc(db, "siteSettings", "contactInfo");
        const infoSnap = await getDoc(infoRef);

        if (mounted && infoSnap.exists()) {
          const d = infoSnap.data();

          // Email + socials (fallback to defaults if not present)
          setContactInfo({
            email: d.email || DEFAULT_EMAIL,
            socials:
            Array.isArray(d.socials) && d.socials.length
            ? d.socials
            : DEFAULT_SOCIALS,
          });

          // Inquiry options preference: per-page > global > defaults
          const globalOpts = Array.isArray(d.inquiryOptions)
            ? d.inquiryOptions
            : [];
          const finalOptions = (
            perPageOptions && perPageOptions.length ? perPageOptions : globalOpts
          ).filter(Boolean);

          if (finalOptions.length) {
            // Deduplicate while preserving order
            const seen = new Set();
            const cleaned = finalOptions.filter((opt) => {
              const key = String(opt).trim();
              if (!key || seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            setInquiryOptions(cleaned);
          } else {
            setInquiryOptions(DEFAULT_INQUIRY_OPTIONS);
          }
        } else if (mounted) {
          setInquiryOptions(
            perPageOptions && perPageOptions.length
              ? perPageOptions
              : DEFAULT_INQUIRY_OPTIONS
          );
          setContactInfo((prev) => ({
            ...prev,
            email: DEFAULT_EMAIL,
            socials: DEFAULT_SOCIALS,
          }));
        }
      } catch (err) {
        console.error("Failed to load contact page data:", err);
        if (mounted) {
          setInquiryOptions(DEFAULT_INQUIRY_OPTIONS);
          setContactInfo({ email: DEFAULT_EMAIL, socials: DEFAULT_SOCIALS });
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Basic client-side validation
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test((email || "").trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // double-submit guard
    setIsSubmitting(true);
    setResponseMessage("");

    try {
      // --- Hardened honeypot checks (UNCONTROLLED DOM via refs + token) ---
      const domHoneypot = (websiteRef.current?.value || "").trim();
      const domToken = (tokenRef.current?.value || "").trim();
      if (domHoneypot !== "" || domToken !== hpToken) {
        console.warn("Spam detected — submission blocked.", {
          domHoneypot,
          domToken,
          hpToken,
        });
        setResponseMessage("Submission blocked.");
        setIsSubmitting(false);
        return;
      }

      // Validate
      if ((formData.name || "").trim().length < 2) {
        throw new Error("Please enter your name.");
      }
      if (!isValidEmail(formData.email)) {
        throw new Error("Please enter a valid email address.");
      }
      if ((formData.message || "").trim().length < 10) {
        throw new Error(
          "Please write a bit more in the message (at least 10 characters)."
        );
      }

      // Ensure inquiry is one of the allowed options
      const safeInquiry = inquiryOptions.includes(formData.inquiry)
        ? formData.inquiry
        : "";

      // Save to Firestore
      await addDoc(collection(db, "contactSubmissions"), {
        name: formData.name.trim(),
        email: formData.email.trim(),
        inquiry: safeInquiry,
        message: formData.message.trim(),
        toEmail: contactInfo.email || DEFAULT_EMAIL, // context for admin view
        receivedAt: serverTimestamp(),
        status: "new",
        read: false,
      });

      setResponseMessage("Thanks! Your message has been received.");

      // Clear visible fields
      setFormData({
        name: "",
        email: "",
        inquiry: "",
        message: "",
      });

      // Clear the hidden honeypot field in the DOM (token stays as-is)
      if (websiteRef.current) websiteRef.current.value = "";
    } catch (err) {
      console.error(err);
      setResponseMessage(
        err.message || "There was an error. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fancy social links (chips)
const renderSocialLinks = () => {
  const entries = Array.isArray(contactInfo.socials)
    ? contactInfo.socials.filter(
        (item) => item && item.url && item.url.trim()
      )
    : [];

  if (!entries.length) return null;

  const renderIcon = (kind) => {
    if (kind === "instagram") {
      return (
        <svg viewBox="0 0 24 24">
          <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm5 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.9a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z" />
        </svg>
      );
    }

    if (kind === "linkedin") {
      return (
        <svg viewBox="0 0 24 24">
          <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5.001 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM14.5 9c-2.04 0-3.5 1.34-3.5 3.64V21h4v-6.3c0-1.22.58-2.2 1.87-2.2 1.13 0 1.63.83 1.63 2.2V21h4v-7.22C22.5 10.7 20.7 9 18.44 9c-1.31 0-2.45.53-3.2 1.42V9h-.74z" />
        </svg>
      );
    }

    if (kind === "youtube") {
      return (
        <svg viewBox="0 0 24 24">
          <path d="M23 7.2a3 3 0 0 0-2.1-2.1C19.3 4.5 12 4.5 12 4.5s-7.3 0-8.9.6A3 3 0 0 0 1 7.2 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.8a3 3 0 0 0 2.1 2.1c1.6.6 8.9.6 8.9.6s7.3 0 8.9-.6A3 3 0 0 0 23 16.8 31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.2zM10 15.5v-7l6 3.5-6 3.5z" />
        </svg>
      );
    }

    if (kind === "discord") {
      return (
        <svg viewBox="0 0 24 24">
          <path d="M20 4a16 16 0 0 0-4.2-1.3l-.2.4a14 14 0 0 1 3.6 1.1A13 13 0 0 0 16 3c-3-.2-6-.2-9 0A13 13 0 0 0 4.8 4.2 14 14 0 0 1 8.4 3l-.2-.3A16 16 0 0 0 4 4C1.6 7.5 1 11 1.2 14.4A16 16 0 0 0 7 17.6l.8-1.2c-1.6-.6-3-1.5-4-2.7.8.6 1.9 1.2 3.1 1.6 2.5.9 5 .9 7.5 0 1.2-.4 2.3-1 3.1-1.6-1 1.2-2.4 2.1-4 2.7l.9 1.2a16 16 0 0 0 5.8-3.2C23 11 22.4 7.5 20 4zM9.5 13c-.8 0-1.5-.8-1.5-1.7 0-.9.7-1.6 1.5-1.6s1.5.7 1.5 1.6S10.3 13 9.5 13zm5 0c-.8 0-1.5-.8-1.5-1.7 0-.9.7-1.6 1.5-1.6s1.5.7 1.5 1.6-.7 1.7-1.5 1.7z" />
        </svg>
      );
    }

    return null;
  };

  return (
    <section className="contact-socials">
      <h3 className="contact-socials-title">Find me on:</h3>

      <nav className="socials-wrap" aria-label="Social links">
        {entries.map((item, idx) => {
          const label = item.label || "Social";
          const url = item.url;
          const kind = item.type || "generic";

          return (
            <a
              key={item.id || `${label}-${idx}`}
              className={`social-chip ${kind}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="chip-icon" aria-hidden="true">
                {renderIcon(kind)}
              </span>

              <span className="chip-text">{label}</span>
              <span className="chip-sparkle" aria-hidden="true" />
            </a>
          );
        })}
      </nav>
    </section>
  );
};

  return (
    <div className="contact-container">
      {/* Hero Section */}
      <div
        className="contact-hero"
        style={{
          backgroundImage: pageSettings.heroImage
            ? `url(${pageSettings.heroImage})`
            : undefined,
        }}
      >
        <div className="contact-hero-overlay" />
        <div className="contact-hero-text left-align">
          <h1>{pageSettings.heroTitle}</h1>
          <p>{pageSettings.heroSubtitle}</p>
        </div>
      </div>

      {/* Info Sections */}
      <div className="contact-info desktop-info">
        <p
          dangerouslySetInnerHTML={{
            __html: (pageSettings.desktopDescription || "").replace(
              /lsulaksha\.shetty@gmail\.com/gi,
              contactInfo.email || DEFAULT_EMAIL
            ),
          }}
        />
      </div>

      <div className="contact-info mobile-info">
        <div
          dangerouslySetInnerHTML={{
            __html: (pageSettings.mobileDescription || "").replace(
              /lsulaksha\.shetty@gmail\.com/gi,
              contactInfo.email || DEFAULT_EMAIL
            ),
          }}
        />
      </div>

      {/* Social chips */}
      {renderSocialLinks()}

      <div className="contact-divider">
        <img src={require("../assets/break.png")} alt="divider" />
      </div>

      {/* Contact Form + Creative Doodle */}
        <div className="contact-main">
        <form className="contact-form" onSubmit={handleSubmit}>
        {/* Honeypot field (hidden, UNCONTROLLED) */}
        <input
          type="text"
          id="website"
          ref={websiteRef}
          autoComplete="off"
          style={{
            position: "absolute",
            left: "-9999px",
            opacity: 0,
            height: 0,
            width: 0,
          }}
          tabIndex={-1}
          aria-hidden="true"
          placeholder="Leave this empty"
          defaultValue=""
        />

        {/* Honeypot token (UNCONTROLLED) */}
        <input
          type="text"
          id="hp_token"
          ref={tokenRef}
          autoComplete="off"
          style={{
            position: "absolute",
            left: "-9999px",
            opacity: 0,
            height: 0,
            width: 0,
          }}
          tabIndex={-1}
          aria-hidden="true"
          defaultValue={hpToken}
        />

        <label htmlFor="name">Name</label>
        <input
          type="text"
          id="name"
          placeholder="Your name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          placeholder="Your email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <label htmlFor="inquiry">Inquiry About</label>
        <select
          id="inquiry"
          value={formData.inquiry}
          onChange={handleChange}
          required
        >
          <option value="">Select an option</option>
          {inquiryOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>

        <label htmlFor="message">Message</label>
        <textarea
          id="message"
          rows="5"
          placeholder="Your message"
          value={formData.message}
          onChange={handleChange}
          required
        />

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Sending..." : "Let’s Create Something Amazing!"}
        </button>

        {responseMessage && (
          <p
            style={{ marginTop: "1rem", fontWeight: "bold" }}
            aria-live="polite"
          >
            {responseMessage}
          </p>
        )}
        </form>

  <div className="contact-doodle-wrap">
    <img
      src={contactDoodle}
      alt=""
      className="contact-doodle"
      aria-hidden="true"
    />
  </div>
</div>

<Footer />
    </div>
  );
};

export default Contact;
